import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { colors, genderColors } from "../../constants/constants";
import TotalUsersIcon from "../../images/svgs/TotalUsersIcon";
import {
  fetchAILiteracyReport,
  fetchGenderReport,
  fetchProficiencyGenderData,
  fetchProficiencyPercentageData,
} from "../../services/adminService";
import AdminDashboardCard from "../Admin/AdminDashboardCard";
import BarChartComponent from "../barChart/BarChart";
import SecondaryLoader from "../SecondaryLoader/SecondaryLoader";
import WelcomeCard from "../WelcomeCard";
import KeyInsights from "../keyInsights";
import GraphCard from "../graphCard/GraphCard";
import PieChartWithDescription from "../graphCard/PieChartWithDescription";

const AdminGenderReport = () => {
  const [aiLiteracyData, setAiLiteracyData] = useState();

  const userId = useSelector((state) => state.auth.id);

  const {
    data: genderReportData,
    isLoading: genderReportLoading,
    error,
  } = useQuery({
    queryKey: ["genderReportData"],
    queryFn: fetchGenderReport,
    enabled: !!userId,
  });

  const male = genderReportData?.data.male;
  const female = genderReportData?.data.female;
  const preferNotToSay = genderReportData?.data.prefer_not_to_say;
  const others = genderReportData?.data.other;

  const cardDetails = [
    {
      label: "Male",
      figure: male?.count || 0,
      description: male?.percentage || 0,
      icon: <TotalUsersIcon />,
    },
    {
      label: "Female",
      figure: female?.count || 0,
      description: female?.percentage || 0,
      icon: <TotalUsersIcon />,
    },
    {
      label: "Prefer not to say",
      figure: preferNotToSay?.count || 0,
      description: preferNotToSay?.percentage || 0,
      icon: <TotalUsersIcon />,
    },
    {
      label: "Others",
      figure: others?.count || 0,
      description: others?.percentage || 0,
      icon: <TotalUsersIcon />,
    },
  ];

  const {
    data: proficiencyGenderData,
    isLoading: proficiencyGenderLoading,
    error: proficiencyGenderDistributionError,
  } = useQuery({
    queryKey: ["proficiencyGenderData"],
    queryFn: fetchProficiencyGenderData,
    enabled: !!userId,
  });

  const getUsedGenderLabels = (data) => {
    const genderSet = new Set();

    Object.values(data || {}).forEach((genders) => {
      Object.keys(genders).forEach((gender) => {
        genderSet.add(gender);
      });
    });

    return Array.from(genderSet);
  };

  const transformStackedBarData = (data) => {
    if (!data) return [];

    const usedGenderLabels = getUsedGenderLabels(data);

    return Object.entries(data).map(([level, genders]) => {
      const genderCounts = {};

      usedGenderLabels.forEach((gender) => {
        genderCounts[gender] = genders[gender] || 0;
      });

      return {
        name: level,
        ...genderCounts,
      };
    });
  };

  const usedGenderLabels = getUsedGenderLabels(proficiencyGenderData);

  const barChartData = transformStackedBarData(proficiencyGenderData);

  const barChartBars = usedGenderLabels.map((gender) => ({
    dataKey: gender,
    color: genderColors[gender] || "#D3D3D3", // fallback to grey
    label: gender,
  }));

  const {
    data: profiicienyByPercentageData,
    isLoading: profiicienyByPercentageLoading,
    error: profiicienyByPercentageError,
  } = useQuery({
    queryKey: ["profiicienyByPercentageData"],
    queryFn: fetchProficiencyPercentageData,
    enabled: !!userId,
  });

  const {
    data: aiLiteracyReportData,
    isLoading,
    error: aiLiteracyReportError,
  } = useQuery({
    queryKey: ["aiLiteracyReportData"],
    queryFn: fetchAILiteracyReport,
    enabled: !!userId,
  });

  const processData = (reportData) => {
    if (!reportData) return [];

    const { totalUsers, ...proficiencyLevels } = reportData;

    return Object.entries(proficiencyLevels)
      .filter(([_, count]) => count > 0) // Filter out zero values
      .map(([level, count], index) => ({
        name: level,
        value: count,
        percentage: Math.round((count / totalUsers) * 100),
        fill: colors[index % colors.length],
      }));
  };

  // Usage in useEffect
  useEffect(() => {
    if (aiLiteracyReportData) {
      const processedData = processData(aiLiteracyReportData);
      setAiLiteracyData(processedData);
    }
  }, [aiLiteracyReportData]);

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard title={"Gender Report"} />
      <>
        {" "}
        {genderReportLoading ? (
          <SecondaryLoader />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {cardDetails.map((card, index) => (
                <AdminDashboardCard
                  key={index}
                  label={card.label}
                  figure={card.figure}
                  description={card.description}
                  icon={card.icon}
                />
              ))}
            </div>
            {proficiencyGenderLoading ? (
              <SecondaryLoader />
            ) : proficiencyGenderDistributionError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl shadow-sm mb-4">
                <strong className="font-semibold">Oops!</strong> Something went
                wrong while loading gender distribution data.
              </div>
            ) : !barChartData.length ? (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-xl shadow-sm mb-4">
                <strong className="font-semibold">Heads up!</strong> No quiz
                gender distribution data available yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-5">
                <GraphCard title="AI Literacy Level Distribution">
                  <div>
                    <PieChartWithDescription data={aiLiteracyData} />
                  </div>
                </GraphCard>
                <div className="w-full">
                  <BarChartComponent
                    title="AI Literacy Breakdown by Gender"
                    data={barChartData}
                    bars={barChartBars}
                    height={400}
                  />
                </div>
              </div>
            )}
            {profiicienyByPercentageData && (
              <KeyInsights
                overviewDescription={`${profiicienyByPercentageData.totalUsers} users, ${profiicienyByPercentageData.emerging}% are at the Emerging level of AI literacy, ${profiicienyByPercentageData.beginner}% at Beginner, ${profiicienyByPercentageData.proficient}% at Proficient, and only ${profiicienyByPercentageData.advanced}% at Advanced level.`}
              />
            )}
          </>
        )}
      </>
    </div>
  );
};

export default AdminGenderReport;
